import type { ConflictCandidatePatient, ConflictRow } from "./types";

/** Portal Axios base already includes `/api`. */
export const UNASSIGNED_INTERACTIONS_URL =
  "/aircall/unassigned-interactions";

export function normalizeCandidatePatient(p: any): ConflictCandidatePatient {
  return {
    _id: String(p?._id ?? p?.id ?? ""),
    patientId: p?.patientId != null ? String(p.patientId) : undefined,
    firstName: p?.firstName,
    middleName: p?.middleName,
    lastName: p?.lastName,
    dateOfBirth: p?.dateOfBirth,
    status: p?.status,
    primaryNumber: p?.primaryNumber,
    secondaryNumber: p?.secondaryNumber,
    organizationName:
      p?.organizationName ??
      (typeof p?.organizationId === "object"
        ? p.organizationId?.organizationName
        : undefined),
    organizationId: p?.organizationId,
    lastActivityAt:
      p?.lastActivityAt ??
      p?.lastVisit ??
      p?.updatedAt ??
      p?.createdAt,
    photoUrl: p?.photoUrl ?? p?.profileImage,
    profileImage: p?.profileImage ?? p?.photoUrl,
  };
}

/**
 * Normalize an unassigned-interaction row from whatever shape the API returns.
 */
export function normalizeUnassignedInteraction(row: any): ConflictRow {
  if (!row || typeof row !== "object") {
    return { _id: "" };
  }

  const rawCandidates =
    row.candidatePatients ??
    row.patients ??
    row.candidate_patients ??
    [];

  const candidates = Array.isArray(rawCandidates)
    ? rawCandidates
        .map(normalizeCandidatePatient)
        .filter((p) => p._id)
    : [];

  const externalNumber =
    row.externalNumber ??
    row.external_number ??
    row.phoneNumber ??
    row.phone_number ??
    row.phone ??
    row.from ??
    row.to;

  return {
    ...row,
    _id: String(row._id ?? row.id ?? ""),
    callAt:
      row.callAt ??
      row.startedAt ??
      row.started_at ??
      row.createdAt ??
      row.created_at,
    createdAt: row.createdAt ?? row.created_at,
    externalNumber:
      externalNumber != null ? String(externalNumber).trim() : undefined,
    phoneNumber:
      externalNumber != null
        ? String(externalNumber).trim()
        : row.phoneNumber ?? row.phone_number ?? row.phone,
    callType:
      row.callType ??
      row.call_type ??
      row.direction ??
      row.type,
    organizationName:
      row.organizationName ??
      row.organization_name ??
      (typeof row.organizationId === "object"
        ? row.organizationId?.organizationName
        : undefined),
    organizationId: row.organizationId ?? row.organization_id,
    recordingUrl:
      row.recordingUrl ??
      row.recording_url ??
      row.recording ??
      row.attachmentUrl ??
      row.attachment_url,
    recordingName:
      row.recordingName ??
      row.recording_name ??
      row.attachmentName ??
      row.attachment_name,
    transcript:
      row.transcript ??
      row.transcription ??
      row.summary ??
      "",
    transcriptionUtterances: Array.isArray(row.transcriptionUtterances)
      ? row.transcriptionUtterances
      : Array.isArray(row.transcription_utterances)
        ? row.transcription_utterances
        : [],
    durationSeconds:
      row.durationSeconds ??
      row.duration_seconds ??
      (typeof row.duration === "number" ? row.duration : undefined),
    duration: typeof row.duration === "string" ? row.duration : row.durationLabel,
    candidatePatients: candidates,
  };
}

/** Extract list from GET /aircall/unassigned-interactions payload.data */
export function extractUnassignedInteractions(dataRoot: any): ConflictRow[] {
  if (!dataRoot) return [];
  if (Array.isArray(dataRoot)) {
    return dataRoot.map(normalizeUnassignedInteraction);
  }
  const list =
    dataRoot.interactions ??
    dataRoot.unassignedInteractions ??
    dataRoot.unassigned_interactions ??
    dataRoot.items ??
    dataRoot.rows ??
    [];
  return Array.isArray(list)
    ? list.map(normalizeUnassignedInteraction)
    : [];
}

/** Extract list from GET /aircall/invalid-interactions payload.data */
export function extractInvalidInteractions(dataRoot: any): ConflictRow[] {
  if (!dataRoot) return [];
  if (Array.isArray(dataRoot)) {
    return dataRoot.map(normalizeUnassignedInteraction);
  }
  const list =
    dataRoot.interactions ??
    dataRoot.invalidInteractions ??
    dataRoot.invalid_interactions ??
    dataRoot.items ??
    dataRoot.rows ??
    [];
  return Array.isArray(list)
    ? list.map(normalizeUnassignedInteraction)
    : [];
}
